import { convertTimeRange } from "@/helpers/time";
import type {
  Dataset,
  DatasetDetailSchemaResponse,
  DatasetFilter,
  DatasetStyle,
  StyleConfig
} from "@/types/dataset";
import { EFilterOptionType } from "@/types/dataset";
import type {
  DatasetConfigAttributeResponse,
  DatasetConfigResponse,
  GridConfigResponse,
  LegendConfig,
  LegendType
} from "@/types/project";
import type { AdminAreaSettings } from "@/types/spatial";

import {
  cacheMarkerImage,
  embedMarkerImageUrl,
  getCachedMarkerImage,
  isLocalImageUrl
} from "./mapLegend";

export type MappedFilter = {
  filterType: EFilterOptionType;
  filter: {
    criteria: string;
    include_nulls: boolean;
  } | null;
  attributeId: string;
  attributeCode: string | undefined;
  is_multiple_adm_area?: boolean;
};

export const DEFAULT_H3_CONFIG = {
  aggregation_type: "AVG" as const,
  level: 8 // Default H3 resolution level
};

export const getMaxLevelAdminArea = (
  obj: AdminAreaSettings | null
): keyof AdminAreaSettings | null => {
  if (!obj) return null;
  let maxLevel: keyof AdminAreaSettings | null = null;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const typedKey = key as keyof AdminAreaSettings;
      if (obj[typedKey] !== undefined && obj[typedKey] !== null) {
        maxLevel = typedKey;
      }
    }
  }

  return maxLevel;
};

export const mapDatasetFilters = (filter: DatasetFilter): MappedFilter => {
  let criteriaAndNulls = null;

  if (filter.filterType === EFilterOptionType.DATE_TIME) {
    criteriaAndNulls = {
      criteria:
        convertTimeRange(filter.dateTimeRange, filter.time_format) ?? "",
      include_nulls: filter.dateTimeRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.MIN_MAX) {
    criteriaAndNulls = {
      criteria: `${filter.minMaxRange?.min}-${filter.minMaxRange?.max}`,
      include_nulls: filter.minMaxRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.MULTIPLE) {
    const isAdminAreaFilter = filter.selectedOptions?.some((option) =>
      option.includes("::")
    );
    criteriaAndNulls = {
      criteria: filter.selectedOptions?.join("|") ?? "",
      include_nulls: false,
      ...(isAdminAreaFilter
        ? { is_multiple_adm_area: filter.is_multiple_adm_area ?? false }
        : {})
    };
  } else if (filter.filterType === EFilterOptionType.DATE) {
    criteriaAndNulls = {
      criteria: `${filter.dateRange?.from || null}/${filter.dateRange?.to || null}`,
      include_nulls: filter.dateRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.TIME) {
    criteriaAndNulls = {
      criteria: `${filter.timeRange?.from || null}/${filter.timeRange?.to || null}`,
      include_nulls: filter.timeRange?.includeNulls ?? false
    };
  }

  return {
    filterType: filter.filterType,
    filter: criteriaAndNulls,
    attributeId: filter.paired_key_attribute_id ?? filter.attributeId,
    attributeCode: filter.attributeCode
  };
};

export const processCatchmentDatasetAttributes = (
  dataset: Dataset,
  filters: DatasetFilter[]
): DatasetConfigAttributeResponse[] => {
  const mappedFilters: MappedFilter[] = filters.map((filter) =>
    mapDatasetFilters(filter)
  );

  /** Track matched filters to prevent the same filter from being matched to
   * multiple attributes. Admin area filters are EXCLUDED from this dedup —
   * they intentionally match every admin level (adm_area_level_1..4) so the
   * downstream `separateAdminAreaAttributes` + `createFinalAttributes` can
   * pick the lowest level (mirrors `processDatasetAttributes` for main
   * datasets). Without this, the first-match-wins dedup would lock the
   * filter to whichever admin level appears first in `dataset.categories`
   * (often the deepest), which the BE then echoes back unchanged on save.
   */
  const matchedFilterKeys = new Set<string>();

  const combinedAttributes: DatasetConfigAttributeResponse[] =
    dataset.categories?.map((attribute) => {
      const attributeId = attribute.paired_key_attribute_id ?? attribute.id;
      let matchedIsAdmArea = false;

      const matchedFilter = mappedFilters.find((f) => {
        const isAdmAreaFilter = f.filter?.criteria?.includes("::");

        if (isAdmAreaFilter) {
          if (f.attributeId === attributeId) {
            matchedIsAdmArea = true;
            return true;
          }
          return false;
        }

        const filterKey = `${f.attributeId}_${f.attributeCode ?? ""}`;
        if (matchedFilterKeys.has(filterKey)) return false;

        return (
          f.attributeId === attributeId &&
          f.attributeCode === attribute.attribute_code
        );
      }) || { filter: null, filterType: null };

      if (matchedFilter.filter && !matchedIsAdmArea) {
        const filterKey = `${(matchedFilter as MappedFilter).attributeId}_${(matchedFilter as MappedFilter).attributeCode}`;
        matchedFilterKeys.add(filterKey);
      }

      const { filter, filterType } = matchedFilter;
      return {
        filter,
        id: attributeId,
        weight_score: null,
        ...(filterType ? { filterType } : {}),
        ...(attribute.attribute_code
          ? { attribute_code: attribute.attribute_code }
          : {})
      };
    }) || [];

  /** Mirror main dataset (`processDatasetAttributes` + `separateAdminAreaAttributes`
   * + `createFinalAttributes`): collapse the admin-level entries into a single
   * primary entry at the lowest level (alphabetical) so the payload matches
   * what a non-catchment dataset with the same filter would produce.
   */
  const { adminAreaAttributes, nonAdminAttributes } =
    separateAdminAreaAttributes(combinedAttributes, dataset);

  return createFinalAttributes(nonAdminAttributes, adminAreaAttributes);
};

export const createFinalAttributes = (
  nonAdminAttributes: DatasetConfigAttributeResponse[],
  adminAreaAttributes: Map<string, DatasetConfigAttributeResponse>
): DatasetConfigAttributeResponse[] => {
  const finalAttributes = [...nonAdminAttributes];

  if (adminAreaAttributes.size > 0) {
    const sortedLevels = Array.from(adminAreaAttributes.keys()).sort((a, b) =>
      a.localeCompare(b)
    );

    const primaryAdminAttr = adminAreaAttributes.get(sortedLevels[0]);
    if (!primaryAdminAttr) return finalAttributes;

    const maxWeightScore = Math.max(
      ...Array.from(adminAreaAttributes.values()).map(
        (attr) => attr.weight_score || 0
      )
    );
    finalAttributes.push({
      ...primaryAdminAttr,
      id: primaryAdminAttr.id,
      weight_score: maxWeightScore
    });
  }

  return finalAttributes;
};

export const separateAdminAreaAttributes = (
  combinedAttributes: DatasetConfigAttributeResponse[],
  primaryDataset: Dataset
): {
  adminAreaAttributes: Map<string, DatasetConfigAttributeResponse>;
  nonAdminAttributes: DatasetConfigAttributeResponse[];
} => {
  const adminAreaAttributes = new Map<string, DatasetConfigAttributeResponse>();
  const nonAdminAttributes: DatasetConfigAttributeResponse[] = [];

  const isH3Dataset =
    primaryDataset.properties.spatialType === "H3" &&
    primaryDataset.data.source === "bvt";

  for (const attr of combinedAttributes) {
    let category: DatasetDetailSchemaResponse | undefined;
    if (isH3Dataset) {
      category = primaryDataset.data.schema.find(
        (item) => (item.paired_key_attribute_id ?? item.id) === attr.id
      );
    } else {
      category = primaryDataset.categories.find(
        (item) => (item.paired_key_attribute_id ?? item.id) === attr.id
      );
    }
    const attributeCode = category?.attribute_code;

    if (attributeCode?.startsWith("adm_area_level_")) {
      adminAreaAttributes.set(attributeCode, attr);
    } else {
      nonAdminAttributes.push(attr);
    }
  }

  return { adminAreaAttributes, nonAdminAttributes };
};

export const getGridConfig = (
  currentDataset: Dataset
): GridConfigResponse | undefined => {
  if (currentDataset?.h3Configuration) {
    return {
      aggregation_type:
        currentDataset?.h3Configuration?.aggregationType ?? "AVG",
      level: currentDataset?.h3Configuration?.resolution ?? 8
    };
  }

  return currentDataset?.properties?.spatialType === "H3" &&
    currentDataset?.data?.source === "bvt"
    ? DEFAULT_H3_CONFIG
    : undefined;
};

/**
 * Maps in-memory legend style to the save payload — `legend_type` (active method)
 * plus `legend_configs` (one config per configured method). Value-based methods
 * (quantile/ordinal) return undefined so they stay legacy null on the wire.
 */
export const mapStyleToLegend = (
  style: DatasetStyle | undefined,
  /** Maps a not-yet-uploaded image (data URL) to its uploaded asset id. */
  assetIdByImageUrl?: Map<string, string>
):
  | {
      legend_type: LegendType;
      legend_configs: Partial<Record<LegendType, LegendConfig>>;
    }
  | undefined => {
  const method = style?.colorScaleMethod;
  // Persist the last-SAVED config (falls back to the live one for layers seeded
  // before the saved slot existed), so unsaved live previews never round-trip.
  const savedSimpleConfig = style?.simpleConfigSaved ?? style?.simpleConfig;
  const savedCustomOrdinalConfig =
    style?.customOrdinalConfigSaved ?? style?.customOrdinalConfig;
  const savedCustomBreaksConfig =
    style?.customBreaksConfigSaved ?? style?.customBreaksConfig;

  const resolveAssetId = (row: {
    imageUrl: string | null;
    imageAssetId: string | null;
    iconId?: string | null;
  }): string | null =>
    // A selected icon replaces any image — never persist both (BE validation
    // rejects a row with `fill_image_asset_id` AND `fill_icon_id`, ORB-3394).
    row.iconId
      ? null
      : (row.imageAssetId ??
        (row.imageUrl ? (assetIdByImageUrl?.get(row.imageUrl) ?? null) : null));

  // Build one LegendConfig per method that has a saved config, so every
  // configured method round-trips (the store keeps all three independently).
  const legendConfigs: Partial<Record<LegendType, LegendConfig>> = {};

  if (savedSimpleConfig) {
    legendConfigs.simple = {
      breaks: null,
      steps: null,
      default: {
        fill_color: savedSimpleConfig.fillColor,
        outline_color: savedSimpleConfig.outlineColor,
        fill_image_asset_id: resolveAssetId(savedSimpleConfig),
        fill_icon_id: savedSimpleConfig.iconId ?? null,
        // Persist the user-editable label so a rename round-trips (restore
        // falls back to the attribute name for legacy records without it).
        name: savedSimpleConfig.label
      }
    };
  }

  if (savedCustomOrdinalConfig) {
    legendConfigs.custom_ordinal = {
      breaks: null,
      steps: savedCustomOrdinalConfig.steps.map((step) => ({
        name: step.label,
        assigned_values: step.values,
        fill_color: step.fillColor,
        outline_color: step.outlineColor,
        fill_image_asset_id: resolveAssetId(step),
        fill_icon_id: step.iconId ?? null
      })),
      default: {
        fill_color: savedCustomOrdinalConfig.otherFillColor,
        outline_color: savedCustomOrdinalConfig.otherOutlineColor,
        fill_image_asset_id: null,
        fill_icon_id: null
      }
    };
  }

  if (savedCustomBreaksConfig) {
    legendConfigs.custom_breaks = {
      steps: null,
      breaks: savedCustomBreaksConfig.steps.map((step) => ({
        name: step.label,
        start: step.start,
        end: step.end,
        fill_color: step.fillColor,
        outline_color: step.outlineColor,
        fill_image_asset_id: resolveAssetId(step),
        fill_icon_id: step.iconId ?? null
      })),
      default: {
        fill_color: savedCustomBreaksConfig.otherFillColor,
        outline_color: savedCustomBreaksConfig.otherOutlineColor,
        fill_image_asset_id: null,
        fill_icon_id: null
      }
    };
  }

  // Only persist when the ACTIVE method is a legend type with a saved config;
  // value-based methods (quantile / ordinal) stay legacy null on the wire.
  const activeType: LegendType | undefined =
    method === "simple" ||
    method === "custom_ordinal" ||
    method === "custom_breaks"
      ? method
      : undefined;
  // Only persist when the active method has a saved config.
  if (!activeType || !legendConfigs[activeType]) return undefined;

  return {
    legend_type: activeType,
    legend_configs: legendConfigs
  };
};

/**
 * Inverse of {@link mapStyleToLegend}: restores every method's config from
 * `legend_configs` into the StyleConfig for `createNewDataset`, with
 * `legend_type` as the active method. Undefined when no legend fields exist.
 */
export const mapLegendToStyleConfig = (
  dataset: DatasetConfigResponse
):
  | Pick<
      StyleConfig,
      | "colorScaleMethod"
      | "simpleConfig"
      | "customOrdinalConfig"
      | "customBreaksConfig"
    >
  | undefined => {
  const legendConfigs = dataset.legend_configs;
  if (!legendConfigs) return undefined;

  const result: Pick<
    StyleConfig,
    | "colorScaleMethod"
    | "simpleConfig"
    | "customOrdinalConfig"
    | "customBreaksConfig"
  > = {};

  const simple = legendConfigs.simple;
  if (simple?.default) {
    result.simpleConfig = {
      fillColor: simple.default.fill_color,
      outlineColor: simple.default.outline_color,
      // Prefer the persisted label; fall back to the attribute name for
      // legacy records saved before the label was carried by the contract.
      label: simple.default.name ?? dataset.color_based?.attribute_name ?? "",
      imageUrl: simple.default.fill_image_url ?? null,
      imageName: null,
      imageAssetId: simple.default.fill_image_asset_id ?? null,
      iconId: simple.default.fill_icon_id ?? null
    };
  }

  const ordinal = legendConfigs.custom_ordinal;
  if (ordinal?.steps) {
    result.customOrdinalConfig = {
      steps: ordinal.steps.map((step) => ({
        label: step.name,
        values: step.assigned_values,
        fillColor: step.fill_color,
        outlineColor: step.outline_color,
        imageUrl: step.fill_image_url ?? null,
        imageName: null,
        imageAssetId: step.fill_image_asset_id ?? null,
        iconId: step.fill_icon_id ?? null
      })),
      otherFillColor: ordinal.default.fill_color,
      otherOutlineColor: ordinal.default.outline_color
    };
  }

  const breaks = legendConfigs.custom_breaks;
  if (breaks?.breaks) {
    result.customBreaksConfig = {
      steps: breaks.breaks.map((step) => ({
        label: step.name ?? "",
        start: step.start,
        end: step.end,
        fillColor: step.fill_color,
        outlineColor: step.outline_color,
        imageUrl: step.fill_image_url ?? null,
        imageName: null,
        imageAssetId: step.fill_image_asset_id ?? null,
        iconId: step.fill_icon_id ?? null
      })),
      otherFillColor: breaks.default.fill_color,
      otherOutlineColor: breaks.default.outline_color
    };
  }

  // The active method shown on open.
  if (dataset.legend_type) result.colorScaleMethod = dataset.legend_type;

  const hasAny =
    result.colorScaleMethod ||
    result.simpleConfig ||
    result.customOrdinalConfig ||
    result.customBreaksConfig;
  return hasAny ? result : undefined;
};

/** A restored legend row whose marker image URL may need embedding. */
type EmbeddableRow = {
  imageUrl: string | null;
  imageAssetId?: string | null;
  iconId?: string | null;
};

const embedRowImage = async <T extends EmbeddableRow>(
  row: T,
  /** Resolves an asset id to its (presigned) URL — used when the response
   * carries only `fill_image_asset_id` and no `fill_image_url` (analysis config). */
  resolveAssetUrl?: (assetId: string) => Promise<string | null>
): Promise<T> => {
  // Icon markers carry no image to embed.
  if (row.iconId) return row;
  const cached = getCachedMarkerImage(row.imageAssetId);
  if (cached) return { ...row, imageUrl: cached };

  // Prefer the presigned `imageUrl` (project GET); otherwise resolve it from the
  // asset id (analysis config GET returns only the id, no URL).
  let sourceUrl = row.imageUrl;
  if (!sourceUrl && row.imageAssetId && resolveAssetUrl) {
    sourceUrl = await resolveAssetUrl(row.imageAssetId);
  }
  if (!sourceUrl) return row;

  const embedded = await embedMarkerImageUrl(sourceUrl);
  if (row.imageAssetId && isLocalImageUrl(embedded)) {
    cacheMarkerImage(row.imageAssetId, embedded);
  }
  return { ...row, imageUrl: embedded };
};

/**
 * Embeds each restored marker image (Simple row + every Custom Ordinal/Breaks
 * step) as a same-origin data URL so it renders on the config panel, legend and
 * — crucially — the map, whose canvas can't read a cross-origin presigned URL.
 */
export const embedLegendConfigImages = async (
  legend: ReturnType<typeof mapLegendToStyleConfig>,
  /** Resolves an asset id to its URL, for responses that omit `fill_image_url`. */
  resolveAssetUrl?: (assetId: string) => Promise<string | null>
): Promise<ReturnType<typeof mapLegendToStyleConfig>> => {
  if (!legend) return legend;

  const [simpleConfig, customOrdinalConfig, customBreaksConfig] =
    await Promise.all([
      legend.simpleConfig
        ? embedRowImage(legend.simpleConfig, resolveAssetUrl)
        : undefined,
      legend.customOrdinalConfig
        ? Promise.all(
            legend.customOrdinalConfig.steps.map((step) =>
              embedRowImage(step, resolveAssetUrl)
            )
          ).then((steps) => ({ ...legend.customOrdinalConfig!, steps }))
        : undefined,
      legend.customBreaksConfig
        ? Promise.all(
            legend.customBreaksConfig.steps.map((step) =>
              embedRowImage(step, resolveAssetUrl)
            )
          ).then((steps) => ({ ...legend.customBreaksConfig!, steps }))
        : undefined
    ]);

  return {
    ...legend,
    ...(simpleConfig && { simpleConfig }),
    ...(customOrdinalConfig && { customOrdinalConfig }),
    ...(customBreaksConfig && { customBreaksConfig })
  };
};
